/**
 * SupplierPicker.js — Autocomplete input for Supplier / Subcontractor selection.
 *
 * Pulls from existing contacts (category: supplier | subcontractor).
 * Allows inline "Add new" to create a quick contact.
 * Calls onSelect({ id, name, email, phone, address, abn }) on confirmation.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Building2, Plus, X, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplierPicker({ value, supplierId, onSelect, placeholder = 'Search supplier…', className = '' }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [allSuppliers, setAllSuppliers] = useState([]);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  // Sync value prop
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Load all suppliers / subcontractors once
  useEffect(() => {
    axios.get(`${API}/contacts?limit=500`)
      .then(res => {
        const contacts = res.data?.data || res.data || [];
        setAllSuppliers(contacts.filter(c =>
          ['supplier', 'subcontractor'].includes(c.category)
        ));
      })
      .catch(() => {});
  }, []);

  // Filter on query change
  useEffect(() => {
    if (!query.trim()) {
      setResults(allSuppliers.slice(0, 8));
      return;
    }
    const q = query.toLowerCase();
    setResults(
      allSuppliers
        .filter(c =>
          c.full_name?.toLowerCase().includes(q) ||
          c.company?.toLowerCase().includes(q)
        )
        .slice(0, 10)
    );
  }, [query, allSuppliers]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setShowAddNew(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (contact) => {
    const displayName = contact.company || contact.full_name || '';
    setQuery(displayName);
    setOpen(false);
    onSelect({
      id: contact.id,
      name: displayName,
      email: contact.email || '',
      phone: contact.phone || contact.mobile || '',
      address: contact.website || '',
      abn: '',
    });
  };

  const handleClear = () => {
    setQuery('');
    onSelect({ id: '', name: '', email: '', phone: '', address: '', abn: '' });
  };

  const handleAddNew = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/contacts`, {
        full_name: newName.trim(),
        company: newName.trim(),
        email: newEmail.trim(),
        category: 'supplier',
      });
      const contact = res.data;
      setAllSuppliers(prev => [...prev, contact]);
      handleSelect(contact);
      setShowAddNew(false);
      setNewName('');
      setNewEmail('');
    } catch (err) {
      console.error('Failed to create contact:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <div className="relative flex items-center">
        <Building2 className="absolute left-2.5 w-3.5 h-3.5 pointer-events-none" style={{ color: '#c9a84c' }} />
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full pl-8 pr-7 py-1.5 text-sm border rounded"
          style={{ borderColor: '#e5e4df', background: '#fff' }}
        />
        {query ? (
          <button
            onClick={handleClear}
            className="absolute right-2 text-gray-300 hover:text-gray-500"
          >
            <X className="w-3 h-3" />
          </button>
        ) : (
          <ChevronDown className="absolute right-2 w-3 h-3 pointer-events-none" style={{ color: '#9ca3af' }} />
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-lg shadow-lg overflow-hidden"
          style={{ background: '#fff', border: '1px solid #e5e4df', minWidth: 220 }}
        >
          {results.length === 0 && !showAddNew && (
            <div className="px-3 py-2 text-xs text-[#9ca3af]">
              No contacts found
            </div>
          )}
          {results.map(contact => (
            <button
              key={contact.id}
              onClick={() => handleSelect(contact)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#f3f2ef] transition-colors"
            >
              <Building2 className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#c9a84c' }} />
              <div className="min-w-0">
                <div className="font-medium text-[#1a1a2e] truncate">
                  {contact.company || contact.full_name}
                </div>
                {contact.company && contact.full_name !== contact.company && (
                  <div className="text-xs text-[#9ca3af] truncate">{contact.full_name}</div>
                )}
              </div>
              <span
                className="ml-auto flex-shrink-0 text-xs px-1.5 py-0.5 rounded"
                style={{
                  background: contact.category === 'subcontractor' ? '#eff6ff' : '#f0fdf4',
                  color: contact.category === 'subcontractor' ? '#1e40af' : '#166534',
                }}
              >
                {contact.category === 'subcontractor' ? 'Sub' : 'Sup'}
              </span>
            </button>
          ))}

          {/* Add new */}
          {!showAddNew ? (
            <button
              onClick={() => { setShowAddNew(true); setNewName(query); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#c9a84c] hover:bg-[#fffbeb] border-t"
              style={{ borderColor: '#e5e4df' }}
            >
              <Plus className="w-3.5 h-3.5" />
              Add "{query || 'new supplier'}"
            </button>
          ) : (
            <div className="p-3 space-y-2 border-t" style={{ borderColor: '#e5e4df' }}>
              <p className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider">Quick Add</p>
              <input
                type="text"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="Company / Name *"
                className="w-full px-2 py-1.5 text-sm border rounded"
                style={{ borderColor: '#e5e4df' }}
                autoFocus
              />
              <input
                type="email"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                placeholder="Email (optional)"
                className="w-full px-2 py-1.5 text-sm border rounded"
                style={{ borderColor: '#e5e4df' }}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowAddNew(false)}
                  className="text-xs px-3 py-1 rounded border"
                  style={{ borderColor: '#e5e4df', color: '#6b7280' }}
                >Cancel</button>
                <button
                  onClick={handleAddNew}
                  disabled={!newName.trim() || saving}
                  className="text-xs px-3 py-1 rounded font-semibold disabled:opacity-40"
                  style={{ background: '#c9a84c', color: '#1a1a2e' }}
                >
                  {saving ? 'Saving…' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
